import React, { useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  Grid,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';

import AdminPage from '../../components/admin/AdminPage';
import SectionCard from '../../components/admin/SectionCard';
import { useToast } from '../../context/ToastContext';
import {
  AdminPointsHistoryRow,
  AdminPointsLookupUser,
  AdminPointsSummary,
} from '../../types';
import { PointsService } from '../../services/PointsService';

const formatPoints = (value?: number) =>
  new Intl.NumberFormat('en-US', {
    minimumFractionDigits: value && value % 1 !== 0 ? 2 : 0,
    maximumFractionDigits: 2,
  }).format(value ?? 0);

const summaryCards = (summary: AdminPointsSummary) => [
  { label: 'Total points', value: summary.totalPoints },
  { label: 'Donation points', value: summary.donationPoints },
  { label: 'Streak approvals', value: summary.streakApprovedPoints },
  { label: 'Shares', value: summary.sharePoints },
];

const PointsConsolePage = () => {
  const { showToast } = useToast();
  const [lookupValue, setLookupValue] = useState('');
  const [lookupError, setLookupError] = useState<string | null>(null);
  const [lookupLoading, setLookupLoading] = useState(false);
  const [selectedUser, setSelectedUser] = useState<AdminPointsLookupUser | null>(null);
  const [summary, setSummary] = useState<AdminPointsSummary | null>(null);
  const [history, setHistory] = useState<AdminPointsHistoryRow[]>([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [historyError, setHistoryError] = useState<string | null>(null);

  const [manualPoints, setManualPoints] = useState('100');
  const [manualNote, setManualNote] = useState('');
  const [manualLoading, setManualLoading] = useState(false);

  const [mockAmount, setMockAmount] = useState('250');
  const [mockCurrency, setMockCurrency] = useState('EGP');
  const [mockCauseId, setMockCauseId] = useState('');
  const [mockChallengeId, setMockChallengeId] = useState('');
  const [mockLoading, setMockLoading] = useState(false);

  const canCreateManualAward = Boolean(
    selectedUser?.id && Number(manualPoints) > 0
  );
  const canCreateMockDonation = Boolean(selectedUser?.id && Number(mockAmount) > 0);

  const cards = useMemo(
    () => (summary ? summaryCards(summary) : []),
    [summary]
  );

  const loadUser = async (identifier = lookupValue) => {
    const trimmed = identifier.trim();
    if (!trimmed) {
      setLookupError('Enter a user email or UUID.');
      return;
    }

    setLookupLoading(true);
    setLookupError(null);
    setHistoryError(null);

    try {
      const lookup = await PointsService.lookupUser(trimmed);
      setSelectedUser(lookup.user);
      setSummary(lookup.summary);
      setHistory([]);

      setHistoryLoading(true);
      try {
        const nextHistory = await PointsService.fetchUserHistory(lookup.user.id);
        setHistory(nextHistory);
      } catch (error: any) {
        console.error(error);
        setHistory([]);
        setHistoryError(
          error?.response?.data?.message ||
            'User loaded, but recent ledger history could not be loaded.'
        );
      } finally {
        setHistoryLoading(false);
      }
    } catch (error: any) {
      console.error(error);
      setSelectedUser(null);
      setSummary(null);
      setHistory([]);
      setHistoryError(null);
      setLookupError(
        error?.response?.data?.message ||
          'Unable to load points for that user. Verify the email/UUID and backend access.'
      );
    } finally {
      setLookupLoading(false);
    }
  };

  const handleCreateMockDonation = async () => {
    if (!selectedUser?.id) {
      setLookupError('Look up a user before creating a mock donation.');
      return;
    }

    setMockLoading(true);
    try {
      const result = await PointsService.createMockDonation({
        userId: selectedUser.id,
        amount: Number(mockAmount),
        currency: mockCurrency.trim().toUpperCase() || 'EGP',
        causeId: mockCauseId.trim() || undefined,
        challengeId: mockChallengeId.trim() || undefined,
      });

      showToast(
        `Mock donation created. ${result.pointsAwarded} points processed with status ${result.pointsStatus}.`
      );
      await loadUser(selectedUser.id);
    } catch (error: any) {
      console.error(error);
      showToast(
        error?.response?.data?.message || 'Failed to create mock donation.',
        'error'
      );
    } finally {
      setMockLoading(false);
    }
  };

  const handleCreateManualAward = async () => {
    if (!selectedUser?.id) {
      setLookupError('Look up a user before awarding manual points.');
      return;
    }

    setManualLoading(true);
    try {
      const result = await PointsService.createManualAward({
        userId: selectedUser.id,
        points: Number(manualPoints),
        note: manualNote.trim() || undefined,
      });

      showToast(result.message);
      await loadUser(selectedUser.id);
    } catch (error: any) {
      console.error(error);
      showToast(
        error?.response?.data?.message || 'Failed to award manual points.',
        'error'
      );
    } finally {
      setManualLoading(false);
    }
  };

  return (
    <AdminPage
      title="Points Console"
      description="Inspect a real user’s AWS-backed points balance and ledger, then trigger a dev/admin mock successful donation without relying on APS."
    >
      <SectionCard
        title="User lookup"
        description="Search by app user email or UUID. This panel reads from the backend, not the local mock admin directory."
      >
        <Stack
          direction={{ xs: 'column', md: 'row' }}
          spacing={2}
          alignItems={{ xs: 'stretch', md: 'center' }}
        >
          <TextField
            fullWidth
            label="User email or UUID"
            value={lookupValue}
            onChange={(event) => setLookupValue(event.target.value)}
            placeholder="user@example.com or 00000000-0000-0000-0000-000000000000"
          />
          <Button
            variant="contained"
            onClick={() => void loadUser()}
            disabled={lookupLoading}
          >
            {lookupLoading ? 'Looking up…' : 'Load points'}
          </Button>
        </Stack>

        {lookupError ? <Alert severity="error">{lookupError}</Alert> : null}

        {selectedUser && summary ? (
          <Stack spacing={2.5}>
            <Box>
              <Typography variant="h6">
                {selectedUser.displayName || selectedUser.email}
              </Typography>
              <Typography color="text.secondary">{selectedUser.email}</Typography>
              <Stack direction="row" spacing={1} sx={{ mt: 1, flexWrap: 'wrap' }}>
                <Chip label={selectedUser.status} size="small" />
                {selectedUser.roles.map((role) => (
                  <Chip key={role} label={role} size="small" variant="outlined" />
                ))}
              </Stack>
            </Box>

            <Grid container spacing={2}>
              {cards.map((card) => (
                <Grid item xs={12} sm={6} md={3} key={card.label}>
                  <Box
                    sx={{
                      p: 2,
                      borderRadius: 3,
                      border: (theme) => `1px solid ${theme.palette.divider}`,
                      backgroundColor: 'background.default',
                    }}
                  >
                    <Typography variant="body2" color="text.secondary">
                      {card.label}
                    </Typography>
                    <Typography variant="h5" sx={{ mt: 0.5 }}>
                      {formatPoints(card.value)}
                    </Typography>
                  </Box>
                </Grid>
              ))}
            </Grid>

            <Typography variant="body2" color="text.secondary">
              Last updated:{' '}
              {summary.updatedAt ? new Date(summary.updatedAt).toLocaleString() : 'Unknown'}
            </Typography>
          </Stack>
        ) : null}
      </SectionCard>

      <SectionCard
        title="Recent ledger"
        description="Every balance change is recorded here as an immutable ledger entry."
      >
        {historyError ? <Alert severity="warning">{historyError}</Alert> : null}

        {historyLoading ? (
          <Typography color="text.secondary">Loading history…</Typography>
        ) : history.length === 0 ? (
          <Typography color="text.secondary">
            {selectedUser ? 'No point transactions found yet.' : 'Look up a user to see history.'}
          </Typography>
        ) : (
          <Box sx={{ overflowX: 'auto' }}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>When</TableCell>
                  <TableCell>Type</TableCell>
                  <TableCell>Reference</TableCell>
                  <TableCell>Points</TableCell>
                  <TableCell>Amount</TableCell>
                  <TableCell>Actor</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {history.map((row) => (
                  <TableRow key={row.id} hover>
                    <TableCell>{new Date(row.createdAt).toLocaleString()}</TableCell>
                    <TableCell>{row.awardType || row.reason}</TableCell>
                    <TableCell>
                      <Stack spacing={0.25}>
                        <Typography fontWeight={600}>
                          {row.referenceType || 'reference'}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {row.referenceId || row.donationId || 'n/a'}
                        </Typography>
                      </Stack>
                    </TableCell>
                    <TableCell>{formatPoints(row.points)}</TableCell>
                    <TableCell>
                      {row.amount !== undefined
                        ? `${row.currency || ''} ${formatPoints(row.amount)}`.trim()
                        : '—'}
                    </TableCell>
                    <TableCell>{row.createdByUserEmail || 'system'}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Box>
        )}
      </SectionCard>

      <SectionCard
        title="Manual points"
        description="Admin-only tool that credits a user directly without creating a donation."
      >
        <Grid container spacing={2}>
          <Grid item xs={12} md={3}>
            <TextField
              label="Points"
              fullWidth
              value={manualPoints}
              onChange={(event) => setManualPoints(event.target.value)}
            />
          </Grid>
          <Grid item xs={12} md={9}>
            <TextField
              label="Note (optional)"
              fullWidth
              value={manualNote}
              onChange={(event) => setManualNote(event.target.value)}
            />
          </Grid>
        </Grid>

        <Stack
          direction={{ xs: 'column', md: 'row' }}
          justifyContent="space-between"
          alignItems={{ xs: 'flex-start', md: 'center' }}
          spacing={2}
        >
          <Typography color="text.secondary">
            {selectedUser
              ? `Target user: ${selectedUser.email}`
              : 'Look up a user first to enable manual points.'}
          </Typography>
          <Button
            variant="contained"
            disabled={!canCreateManualAward || manualLoading}
            onClick={() => void handleCreateManualAward()}
          >
            {manualLoading ? 'Awarding…' : 'Award manual points'}
          </Button>
        </Stack>
      </SectionCard>

      <SectionCard
        title="Mock donation"
        description="Development/admin-only tool that creates a successful donation record and runs the same backend points-award path used for real confirmed donations."
      >
        <Grid container spacing={2}>
          <Grid item xs={12} md={3}>
            <TextField
              label="Amount"
              fullWidth
              value={mockAmount}
              onChange={(event) => setMockAmount(event.target.value)}
            />
          </Grid>
          <Grid item xs={12} md={2}>
            <TextField
              label="Currency"
              fullWidth
              value={mockCurrency}
              onChange={(event) => setMockCurrency(event.target.value)}
            />
          </Grid>
          <Grid item xs={12} md={3.5}>
            <TextField
              label="Cause ID (optional)"
              fullWidth
              value={mockCauseId}
              onChange={(event) => setMockCauseId(event.target.value)}
            />
          </Grid>
          <Grid item xs={12} md={3.5}>
            <TextField
              label="Challenge ID (optional)"
              fullWidth
              value={mockChallengeId}
              onChange={(event) => setMockChallengeId(event.target.value)}
            />
          </Grid>
        </Grid>

        <Stack
          direction={{ xs: 'column', md: 'row' }}
          justifyContent="space-between"
          alignItems={{ xs: 'flex-start', md: 'center' }}
          spacing={2}
        >
          <Typography color="text.secondary">
            {selectedUser
              ? `Target user: ${selectedUser.email}`
              : 'Look up a user first to enable mock donations.'}
          </Typography>
          <Button
            variant="contained"
            disabled={!canCreateMockDonation || mockLoading}
            onClick={() => void handleCreateMockDonation()}
          >
            {mockLoading ? 'Creating…' : 'Create mock success donation'}
          </Button>
        </Stack>
      </SectionCard>
    </AdminPage>
  );
};

export default PointsConsolePage;
